import { partition } from 'lodash';
import type {
  ComponentOrReferenceFormattingRule,
  FormattingRule,
} from '@ardoq/data-model';
import {
  APIEntityType,
  EMPTY,
  isListFieldFormatting,
  type ListFieldFormatting,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { QueryBuilderFilterType } from '@ardoq/query-builder';
import type { Formatting, FormattingGroup } from './types';
import { filterGroupsMap } from './filterGroupsMap';
import type { I18nLocale } from '@ardoq/i18n';

const isComponentOrReferenceRule = (
  rule: FormattingRule
): rule is ComponentOrReferenceFormattingRule =>
  rule.type !== APIEntityType.TAG;

type MapFilterGroupsArgs = {
  i18nLocale: I18nLocale;
  conditionalFormattingRules: FormattingRule[];
  componentsFormattingGroups: FormattingGroup[];
  referencesFormattingGroups: FormattingGroup[];
  formattingForInvalidRuleByRuleId: Partial<Record<string, Formatting>>;
  isNewConditionalFormatting: boolean;
  componentNameAndFieldsIndex: Map<string, string[]>;
  referenceNameAndFieldsIndex: Map<string, string[]>;
};
export const mapFilterGroups = ({
  i18nLocale,
  conditionalFormattingRules,
  componentsFormattingGroups,
  referencesFormattingGroups,
  formattingForInvalidRuleByRuleId,
  componentNameAndFieldsIndex,
  referenceNameAndFieldsIndex,
  isNewConditionalFormatting,
}: MapFilterGroupsArgs): Partial<Record<string, FormattingGroup[]>> => {
  const [componentFormattingRules, referenceFormattingRules] = partition(
    conditionalFormattingRules.filter(isComponentOrReferenceRule),
    ({ type }) => type === APIEntityType.COMPONENT
  );
  const filterGroupsByRuleId = {
    ...filterGroupsMap({
      i18nLocale,
      formattingRules: componentFormattingRules,
      filterGroups: componentsFormattingGroups,
      invalidRulesFormatting: formattingForInvalidRuleByRuleId,
      typeNameAndFieldsIndex: componentNameAndFieldsIndex,
      isNewConditionalFormatting,
    }),
    ...filterGroupsMap({
      i18nLocale,
      formattingRules: referenceFormattingRules,
      filterGroups: referencesFormattingGroups,
      invalidRulesFormatting: formattingForInvalidRuleByRuleId,
      typeNameAndFieldsIndex: referenceNameAndFieldsIndex,
      isNewConditionalFormatting,
    }),
  };
  return filterGroupsByRuleId;
};

export const mapFormatting = (
  filterGroupsByRuleId: Partial<Record<string, FormattingGroup[]>>
) =>
  Object.fromEntries(
    Object.entries(filterGroupsByRuleId)
      .map(
        ([ruleId, filterGroups]):
          | [string, Partial<Record<string, Formatting>>]
          | null =>
          filterGroups
            ? [
                ruleId,
                Object.fromEntries(
                  filterGroups.flatMap(({ filters }) =>
                    filters.map(filter => [filter.id, filter])
                  )
                ),
              ]
            : null
      )
      .filter(isTruthy)
  );

/**
 * Re-sorts the keys of a stored {@link ListFieldFormatting} to match the
 * canonical field option order defined by `selectOptions`, with `EMPTY` last.
 *
 * The conditional formatting rule may have been persisted with the list field options in arbitrary order.
 * Calling this function ensures that the rule editor and the view legend
 * always display options in field-definition order.
 */
export const sortListFieldFormatting = (
  value: ListFieldFormatting,
  listFieldOptions: string[]
): ListFieldFormatting =>
  Object.fromEntries([
    ...listFieldOptions
      .filter(key => key in value)
      .map(key => [key, value[key]]),
    ...(EMPTY in value ? [[EMPTY, value[EMPTY]]] : []),
  ]) as ListFieldFormatting;

/**
 * Returns the {@link ListFieldFormatting} to use when rendering a list field
 * conditional formatting rule.
 *
 * - **Fresh rule** (`rule.value` is a plain string or null): builds a new
 *   `ListFieldFormatting` from `selectOptions`, pre-filling the matching
 *   option with `rule.color` and leaving the rest transparent.
 *
 * - **Stored rule** (`rule.value` is already a `ListFieldFormatting`): the
 *   backend may return keys in arbitrary insertion order, so we re-sort them
 *   to match the canonical `selectOptions` order. This ensures the rule editor
 *   and the view legend always display options in field-definition order.
 */
const getInitialListFieldFormatting = (
  rule: ComponentOrReferenceFormattingRule,
  listFieldOptions: string[]
): ListFieldFormatting => {
  const { value, color } = rule;

  if (value && isListFieldFormatting(value)) {
    return sortListFieldFormatting(value, listFieldOptions);
  }
  return {
    ...Object.fromEntries(
      listFieldOptions.map(key => [
        key,
        {
          color: key === value ? color : '',
          isDisabled: false,
        },
      ])
    ),
    [EMPTY]: { color: '', isDisabled: false },
  };
};

export const maybeGetInitialListFieldFormatting = (
  rule: ComponentOrReferenceFormattingRule,
  formatting: Formatting
) => {
  const { value } = rule;
  const { filterType, selectOptions } = formatting;
  if (filterType !== QueryBuilderFilterType.SELECT) {
    return value;
  }
  if (!selectOptions) {
    return value;
  }
  return getInitialListFieldFormatting(
    rule,
    selectOptions.map(({ value }) => value)
  );
};

export const mapListFieldFormatting = (
  conditionalFormattingRules: FormattingRule[],
  formattingByRuleAndField: Partial<
    Record<string, Partial<Record<string, Formatting>>>
  >
) =>
  Object.fromEntries(
    conditionalFormattingRules
      .filter(isComponentOrReferenceRule)
      .map((rule): [string, ListFieldFormatting] | null => {
        const { __id: ruleId, field } = rule;
        const formatting = field
          ? formattingByRuleAndField[ruleId]?.[field]
          : null;
        if (formatting?.filterType !== QueryBuilderFilterType.SELECT) {
          return null;
        }
        const { selectOptions } = formatting;
        if (!selectOptions) {
          return null;
        }
        const listFieldFormatting = getInitialListFieldFormatting(
          rule,
          selectOptions.map(({ value }) => value)
        );

        return [ruleId, listFieldFormatting];
      })
      .filter(isTruthy)
  );
